import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  Inbox,
  Loader2,
  Mail,
  MessageCircle,
  RefreshCw,
  Search,
  UserCheck,
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { useCommunicationsWorkspace } from '../../context/CommunicationsWorkspaceContext';
import API_BASE from '../../utils/apiBase';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import PersonMessageComposer from './PersonMessageComposer';
import WhatsAppAttachment from './WhatsAppAttachment';
import {
  buildApplicantActionsDraft,
  communicationDraftKey,
  isRecruitmentReadOnly,
  splitOnboardingActions,
} from '../../lib/communicationsWorkspace';
import {
  recruitmentInboxPollInterval,
  recruitmentMessageState,
} from '../../pages/portal/recruitmentInboxHelpers';

const API = API_BASE;

function displayName(thread) {
  return thread?.contact_name || thread?.company_name || thread?.contact_email || 'Applicant';
}

function formatWhen(value) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' }).format(parsed);
}

function actionTitle(action) {
  return String(action?.title || action?.label || action?.key || 'Outstanding action').trim();
}

function MessageCard({ message }) {
  const outbound = String(message?.direction || '').toLowerCase() === 'outbound';
  const state = recruitmentMessageState(message);
  const channel = String(message?.channel || 'email').toLowerCase();
  return (
    <article className={`flex ${outbound ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[88%] rounded-xl border p-3 text-sm shadow-sm ${
        outbound ? 'border-primary/20 bg-primary/5' : 'border-slate-200 bg-white'
      }`}>
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
          <span className="font-semibold text-slate-700">{outbound ? 'Osabea' : 'Applicant'}</span>
          <span>·</span>
          <span>{channel === 'whatsapp' ? 'WhatsApp' : 'Email'}</span>
          <span>·</span>
          <span>{state.label}</span>
          <span>·</span>
          <span>{formatWhen(message?.created_at)}</span>
        </div>
        {message?.subject ? <p className="mt-2 font-semibold text-slate-900">{message.subject}</p> : null}
        <p className="mt-2 whitespace-pre-wrap break-words text-slate-700">
          {message?.body_text || 'No message body stored.'}
        </p>
        <WhatsAppAttachment message={message} />
      </div>
    </article>
  );
}

export default function CommunicationsWorkspaceDrawer() {
  const { token } = useAuth();
  const {
    canUse,
    open,
    setOpen,
    selection,
    threads,
    threadsLoading,
    refreshThreads,
    selectConversation,
    drafts,
    ensureDraft,
    updateDraft,
  } = useCommunicationsWorkspace();

  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [onboardingPayload, setOnboardingPayload] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [contextExpanded, setContextExpanded] = useState(false);

  const selectedThread = detail?.thread || null;
  const messages = Array.isArray(detail?.messages) ? detail.messages : [];
  const employeeId = String(selection?.employeeId || selectedThread?.entity_id || '').trim();
  const onboardingSync = onboardingPayload?.onboarding_sync || null;
  const { worker: workerActions, admin: adminActions } = splitOnboardingActions(onboardingSync || {});
  const progress = onboardingSync?.applicant_progress || onboardingSync?.wizard_progress || onboardingPayload?.wizard_progress || {};
  const readOnly = isRecruitmentReadOnly(onboardingSync || {});
  const draftKey = communicationDraftKey({
    threadId: selectedThread?.id || selection?.threadId,
    employeeId,
  });
  const draft = drafts[draftKey] || { subject: '', body: '', channel: 'email' };

  const filteredThreads = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return threads;
    return threads.filter((thread) => (
      [displayName(thread), thread?.contact_email, thread?.subject, thread?.last_message_preview]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    ));
  }, [search, threads]);

  const loadSelection = useCallback(async () => {
    if (!open || !token || !selection) return;
    setLoadingDetail(true);
    setDetailError('');
    try {
      let commsResponse;
      if (selection.threadId) {
        commsResponse = await fetch(`${API}/portal-inbox/threads/${encodeURIComponent(selection.threadId)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else if (selection.employeeId) {
        commsResponse = await fetch(`${API}/employees/${encodeURIComponent(selection.employeeId)}/communications`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      if (!commsResponse?.ok) throw new Error('Conversation could not be loaded');
      const comms = await commsResponse.json();
      setDetail(comms || null);

      const resolvedEmployeeId = String(selection.employeeId || comms?.thread?.entity_id || '').trim();
      if (comms?.thread?.id && (
        selection.threadId !== comms.thread.id
        || selection.employeeId !== (resolvedEmployeeId || null)
      )) {
        selectConversation({
          threadId: comms.thread.id,
          employeeId: resolvedEmployeeId || null,
        });
      }

      if (resolvedEmployeeId) {
        const syncResponse = await fetch(
          `${API}/admin/employees/${encodeURIComponent(resolvedEmployeeId)}/onboarding-sync`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (syncResponse.ok) {
          setOnboardingPayload(await syncResponse.json());
        } else {
          setOnboardingPayload(null);
        }
      } else {
        setOnboardingPayload(null);
      }

      await refreshThreads();
    } catch (error) {
      setDetail(null);
      setOnboardingPayload(null);
      setDetailError(error?.message || 'Conversation could not be loaded');
    } finally {
      setLoadingDetail(false);
    }
  }, [open, token, selection, refreshThreads, selectConversation]);

  useEffect(() => {
    loadSelection();
  }, [loadSelection, refreshVersion]);

  useEffect(() => {
    if (!open || !selection || loadingDetail) return undefined;
    const id = window.setTimeout(
      () => setRefreshVersion((value) => value + 1),
      recruitmentInboxPollInterval(messages),
    );
    return () => window.clearTimeout(id);
  }, [open, selection, messages, loadingDetail]);

  useEffect(() => {
    const handleUpdated = (event) => {
      const changedEmployee = String(event?.detail?.employeeId || '');
      if (!employeeId || changedEmployee === employeeId) {
        setRefreshVersion((value) => value + 1);
      }
    };
    window.addEventListener('careops:recruitment-communication-updated', handleUpdated);
    return () => window.removeEventListener('careops:recruitment-communication-updated', handleUpdated);
  }, [employeeId]);

  useEffect(() => {
    setContextExpanded(false);
  }, [selection?.threadId, selection?.employeeId]);

  useEffect(() => {
    if (!selectedThread && !employeeId) return;
    const initialChannel = messages.some((message) => String(message?.channel || '').toLowerCase() === 'whatsapp')
      ? 'whatsapp'
      : 'email';
    ensureDraft(draftKey, { channel: initialChannel });
  }, [draftKey, employeeId, ensureDraft, messages, selectedThread]);

  const insertApplicantActions = () => {
    const nextBody = buildApplicantActionsDraft({
      firstName: String(selectedThread?.contact_name || '').trim().split(/\s+/)[0],
      actions: workerActions,
    });
    if (!nextBody) return;
    updateDraft(draftKey, { ...draft, body: nextBody });
  };

  const selectThread = (thread) => {
    selectConversation({
      threadId: thread?.id,
      employeeId: thread?.entity_id,
    });
  };

  if (!canUse) return null;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="flex w-full flex-col overflow-hidden p-0 sm:w-[720px] sm:max-w-[760px]"
        data-testid="communications-workspace-drawer"
      >
        <SheetHeader className="shrink-0 border-b border-slate-200 px-5 py-4 pr-12 text-left">
          <SheetTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-primary" />
            Communications
          </SheetTitle>
          <SheetDescription>
            Keep the current CareOps page open while you read and reply.
          </SheetDescription>
        </SheetHeader>

        {!selection ? (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="shrink-0 border-b border-slate-200 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Recruitment Inbox</p>
                  <p className="text-xs text-slate-500">Unread conversations are shown first.</p>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={refreshThreads} disabled={threadsLoading}>
                  <RefreshCw className={`h-4 w-4 ${threadsLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              <label className="mt-3 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <Search className="h-4 w-4 text-slate-400" />
                <span className="sr-only">Search recruitment conversations</span>
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search applicant, email or message"
                  className="w-full bg-transparent text-sm outline-none"
                />
              </label>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {threadsLoading && filteredThreads.length === 0 ? (
                <div className="flex h-full items-center justify-center gap-2 text-sm text-slate-500">
                  <Loader2 className="h-5 w-5 animate-spin" /> Loading messages
                </div>
              ) : filteredThreads.length === 0 ? (
                <div className="flex h-full min-h-[320px] flex-col items-center justify-center p-8 text-center text-slate-500">
                  <Inbox className="mb-3 h-10 w-10 text-slate-300" />
                  <p className="font-semibold text-slate-800">No recruitment conversations found</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filteredThreads.map((thread) => (
                    <button
                      key={thread.id}
                      type="button"
                      onClick={() => selectThread(thread)}
                      className="w-full px-5 py-4 text-left hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                      data-testid={`communications-thread-${thread.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-900">{displayName(thread)}</p>
                          <p className="truncate text-xs text-slate-500">{thread.contact_email || 'No email'}</p>
                          <p className="mt-1 truncate text-xs text-slate-600">{thread.last_message_preview || thread.subject || 'No messages yet'}</p>
                        </div>
                        {Number(thread.unread_count || 0) > 0 ? (
                          <span className="rounded-full bg-red-600 px-2 py-0.5 text-[11px] font-bold text-white">
                            {thread.unread_count} unread
                          </span>
                        ) : null}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="shrink-0 border-t border-slate-200 p-4">
              <Button asChild variant="outline" className="w-full">
                <Link to="/portal/inbox?department=recruitment" onClick={() => setOpen(false)}>
                  Open full Recruitment Inbox
                </Link>
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="shrink-0 border-b border-slate-200 bg-white p-4">
              <Button type="button" variant="ghost" size="sm" onClick={() => selectConversation(null)} className="mb-2">
                <ArrowLeft className="mr-1.5 h-4 w-4" /> All messages
              </Button>

              {loadingDetail && !selectedThread ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading conversation
                </div>
              ) : detailError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                  <AlertTriangle className="mr-2 inline h-4 w-4" /> {detailError}
                </div>
              ) : selectedThread ? (
                <>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-lg font-semibold text-slate-950">{displayName(selectedThread)}</p>
                      <p className="text-sm text-slate-500">{selectedThread.contact_email || 'No applicant email'}</p>
                    </div>
                    <Button asChild variant="outline" size="sm">
                      <Link to={employeeId ? `/portal/recruitment/${employeeId}` : '/portal/recruitment'}>
                        <UserCheck className="mr-1.5 h-4 w-4" /> Applicant record
                      </Link>
                    </Button>
                  </div>

                  {onboardingSync ? (
                    <div className="mt-3 overflow-hidden rounded-lg border border-slate-200 bg-slate-50" data-testid="communications-applicant-context">
                      <button
                        type="button"
                        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
                        onClick={() => setContextExpanded((value) => !value)}
                        aria-expanded={contextExpanded}
                        aria-label="Recruitment context"
                      >
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-semibold text-slate-900">
                              {onboardingSync.applicant_status_label || onboardingSync.current_step_title || 'In progress'}
                            </span>
                            <span className="text-xs text-slate-500">{Number(progress?.percentage ?? 0)}% completed/submitted</span>
                            {workerActions.length ? (
                              <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-900">
                                {workerActions.length} applicant action{workerActions.length === 1 ? '' : 's'}
                              </span>
                            ) : null}
                          </div>
                          {onboardingSync.next_worker_action?.title ? (
                            <p className="mt-0.5 truncate text-xs text-amber-900">
                              Next: {onboardingSync.next_worker_action.title}
                            </p>
                          ) : null}
                        </div>
                        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-500 transition-transform ${contextExpanded ? 'rotate-180' : ''}`} />
                      </button>

                      {contextExpanded ? (
                        <div className="grid gap-2 border-t border-slate-200 p-3 sm:grid-cols-2">
                          <div className="rounded-lg border border-amber-200 bg-white p-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">Needs from applicant</p>
                            {workerActions.length ? (
                              <ul className="mt-2 space-y-1 text-xs text-amber-950">
                                {workerActions.slice(0, 6).map((action) => <li key={action.key || actionTitle(action)}>• {actionTitle(action)}</li>)}
                              </ul>
                            ) : (
                              <p className="mt-2 text-xs text-slate-500">No applicant-owned action is currently outstanding.</p>
                            )}
                          </div>

                          <div className="rounded-lg border border-blue-200 bg-white p-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-blue-800">Osabea action</p>
                            {adminActions.length ? (
                              <ul className="mt-2 space-y-1 text-xs text-blue-950">
                                {adminActions.slice(0, 6).map((action) => <li key={action.key || actionTitle(action)}>• {actionTitle(action)}</li>)}
                              </ul>
                            ) : (
                              <p className="mt-2 text-xs text-slate-500">No Osabea-owned action is currently outstanding.</p>
                            )}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50 p-4" data-testid="communications-conversation-history">
              {messages.length === 0 ? (
                <div className="flex min-h-[200px] items-center justify-center text-sm text-slate-500">
                  No messages in this recruitment conversation yet.
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map((message) => <MessageCard key={message.id} message={message} />)}
                </div>
              )}
            </div>

            {selectedThread && employeeId ? (
              <div className="max-h-[40%] shrink-0 overflow-y-auto border-t border-slate-200 bg-white p-3">
                {workerActions.length && !readOnly ? (
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-xs text-slate-500">
                      {workerActions.length} applicant action{workerActions.length === 1 ? '' : 's'} available to insert
                    </p>
                    <Button type="button" size="sm" variant="outline" onClick={insertApplicantActions}>
                      Insert applicant actions
                    </Button>
                  </div>
                ) : null}
                {readOnly ? (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                    <CheckCircle2 className="mr-2 inline h-4 w-4" />
                    Recruitment history is read-only after activation. Use Staff Communications for new staff messages.
                  </div>
                ) : (
                  <PersonMessageComposer
                    key={draftKey}
                    domain="recruitment"
                    employeeId={employeeId}
                    token={token}
                    messages={messages}
                    threadId={selectedThread.id}
                    emailEnabled
                    readOnly={false}
                    personFirstName={String(displayName(selectedThread)).split(/\s+/)[0]}
                    nextActionTitle={onboardingSync?.next_worker_action?.title || ''}
                    initialChannel={draft.channel}
                    draft={draft}
                    onDraftChange={(nextDraft) => updateDraft(draftKey, nextDraft)}
                    onSent={() => setRefreshVersion((value) => value + 1)}
                  />
                )}
                <div className="mt-2 flex items-center justify-between gap-2 border-t border-slate-100 pt-2">
                  <p className="truncate text-[10px] text-slate-400">Sensitive evidence stays in CareOps.</p>
                  <Button asChild variant="ghost" size="sm">
                    <Link
                      to={`/portal/inbox?department=recruitment&thread=${encodeURIComponent(selectedThread.id)}`}
                      onClick={() => setOpen(false)}
                    >
                      Open full Inbox
                    </Link>
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
